import { PLAYER_POSITION_ORDER } from "common/src/players-grid/utils/constants";
import type { Key } from "i18n";

export enum PlayerSortKey {
	Rating = "rating",
	Name = "name",
	Position = "position",
	JerseyNumber = "jerseyNumber",
}

export enum PlayerSortDirection {
	Asc = "asc",
	Desc = "desc",
}

export interface PlayerSort {
	key: PlayerSortKey;
	direction: PlayerSortDirection;
}

export interface SortablePlayer {
	gamePlayerId: number;
	name: string | null;
	position: string | null;
	numberSign: number | null;
	rating: number | null;
}

type PlayerSortOption = {
	key: PlayerSortKey;
	labelKey: Key;
};

const I18N_PREFIX = "game.coachReviews.playerList.sortButton.option";

export const PLAYER_SORT_OPTIONS = [
	{ key: PlayerSortKey.Rating, labelKey: `${I18N_PREFIX}.rating` },
	{ key: PlayerSortKey.Name, labelKey: `${I18N_PREFIX}.name` },
	{ key: PlayerSortKey.Position, labelKey: `${I18N_PREFIX}.position` },
	{ key: PlayerSortKey.JerseyNumber, labelKey: `${I18N_PREFIX}.jerseyNumber` },
] satisfies PlayerSortOption[];

export const getNextPlayerSort = (current: PlayerSort | null, key: PlayerSortKey): PlayerSort | null => {
	if (current?.key !== key) return { key, direction: PlayerSortDirection.Asc };

	return current.direction === PlayerSortDirection.Asc ? { key, direction: PlayerSortDirection.Desc } : null;
};

// null means the player has no value to sort by and is pushed to the end of the list
const getSortValue = (player: SortablePlayer, key: PlayerSortKey): number | string | null => {
	switch (key) {
		case PlayerSortKey.Rating:
			return player.rating;
		case PlayerSortKey.Name:
			return player.name?.trim() || null;
		case PlayerSortKey.JerseyNumber:
			return player.numberSign;
		case PlayerSortKey.Position: {
			const order = PLAYER_POSITION_ORDER.findIndex((position) => position === player.position);
			return order === -1 ? null : order;
		}
	}
};

const compareValues = (a: number | string, b: number | string): number => {
	if (typeof a === "number" && typeof b === "number") return a - b;

	return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

// Keeps the order stable for players that compare equal on the selected key
const compareFallback = (a: SortablePlayer, b: SortablePlayer): number => {
	return (a.name || "").localeCompare(b.name || "") || a.gamePlayerId - b.gamePlayerId;
};

export const sortPlayers = <T extends SortablePlayer>(players: T[], sort: PlayerSort | null): T[] => {
	if (!sort) return players;

	return [...players].sort((a, b) => {
		const aValue = getSortValue(a, sort.key);
		const bValue = getSortValue(b, sort.key);

		// Players without a value stay at the end in both directions
		if (aValue === null || bValue === null) {
			if (aValue === bValue) return compareFallback(a, b);

			return aValue === null ? 1 : -1;
		}

		const result = compareValues(aValue, bValue);

		if (result === 0) return compareFallback(a, b);

		return sort.direction === PlayerSortDirection.Asc ? result : -result;
	});
};
